import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import PhoneInput from '../components/ui/phone-input';

// Password rules — minimum 6 chars, at least one letter, one number, one
// non-alphanumeric character. Each rule is shown to the user with a live
// tick/cross so they can see exactly why their password isn't yet acceptable.
const PASSWORD_RULES = [
  { id: 'len',     label: 'At least 6 characters',         test: (p) => p.length >= 6 },
  { id: 'letter',  label: 'Contains a letter (a–z)',       test: (p) => /[a-zA-Z]/.test(p) },
  { id: 'number',  label: 'Contains a number (0–9)',       test: (p) => /\d/.test(p) },
  { id: 'symbol',  label: 'Contains a special character',  test: (p) => /[^a-zA-Z0-9]/.test(p) },
];

const passwordIsStrong = (p) => PASSWORD_RULES.every((r) => r.test(p));

const CustomerSignup = () => {
  const { signup } = useCustomerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const passwordsMatch = form.password && form.password === form.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!passwordIsStrong(form.password)) {
      toast.error('Please choose a stronger password');
      setShowRules(true);
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    const { success, error, linkedBookings } = await signup(form);
    setSubmitting(false);
    if (success) {
      if (linkedBookings > 0) {
        toast.success(`Account created · ${linkedBookings} past booking${linkedBookings === 1 ? '' : 's'} added to your account`);
      } else {
        toast.success('Account created — welcome!');
      }
      navigate('/my-trips');
    } else {
      toast.error(error);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-gray-50">
      <Card className="max-w-md mx-auto p-8" data-testid="customer-signup-card">
        <h1 className="text-3xl font-bold mb-2 text-black">Create an account</h1>
        <p className="text-sm text-gray-600 mb-8">
          Save your details, view past trips and book again in one click.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-2 block text-gray-700">First name</Label>
              <Input value={form.firstName} onChange={(e) => update('firstName', e.target.value)} data-testid="signup-first-name" />
            </div>
            <div>
              <Label className="mb-2 block text-gray-700">Last name</Label>
              <Input value={form.lastName} onChange={(e) => update('lastName', e.target.value)} data-testid="signup-last-name" />
            </div>
          </div>
          <div>
            <Label className="mb-2 block text-gray-700">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} autoComplete="email" data-testid="signup-email" />
          </div>
          <div>
            <Label className="mb-2 block text-gray-700">Phone</Label>
            <PhoneInput value={form.phone} onChange={(v) => update('phone', v || '')} data-testid="signup-phone" />
          </div>
          <div>
            <Label className="mb-2 block text-gray-700">Password</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              onFocus={() => setShowRules(true)}
              autoComplete="new-password"
              data-testid="signup-password"
            />
            {showRules && (
              <ul className="mt-2 space-y-1 text-xs" data-testid="password-rules">
                {PASSWORD_RULES.map((rule) => {
                  const ok = rule.test(form.password);
                  return (
                    <li key={rule.id} className={`flex items-center gap-1.5 ${ok ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div>
            <Label className="mb-2 block text-gray-700">Repeat password</Label>
            <Input
              type="password"
              value={form.confirmPassword}
              onChange={(e) => update('confirmPassword', e.target.value)}
              autoComplete="new-password"
              data-testid="signup-password-confirm"
              className={
                form.confirmPassword && !passwordsMatch
                  ? 'border-red-300 focus-visible:ring-red-300'
                  : ''
              }
            />
            {form.confirmPassword && !passwordsMatch && (
              <p className="text-xs text-red-600 mt-1">Passwords don't match</p>
            )}
          </div>
          <Button
            type="submit"
            className="w-full bg-black text-white hover:bg-gray-800 h-11 font-semibold mt-2"
            disabled={submitting || !passwordIsStrong(form.password) || !passwordsMatch}
            data-testid="signup-submit"
          >
            {submitting ? 'Creating account…' : 'Create Account'}
          </Button>
        </form>
        <p className="mt-6 text-sm text-center text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-black font-semibold underline">Sign in</Link>
        </p>
      </Card>
    </div>
  );
};

export default CustomerSignup;
